// Settings > Digital Waste Tracking (Defra Receipt of Waste API).
//
// Admin-only. Environment, reporting mode (off / manual / automatic), the start date,
// the received-at source, the Defra API code, receiver and carrier details, and a
// go-live checklist showing which prerequisites are met. The API code is write-only:
// the server returns only a masked value, and leaving the field blank keeps the stored
// code. The vendor credentials are server environment variables; only a yes/no for
// "present" is ever shown.

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Check, CircleDashed, ListChecks, Save } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import {
  fetchDwtSettings,
  saveDwtSettings,
  type DwtModeValue,
  type DwtReason,
  type DwtReceivedAtSource,
  type DwtSettingsDto,
} from '../../../api/dwtSettings';
import { fetchProfileProducts } from '../../../api/dwt';
import { useToast } from '../../../hooks/useToast';
import { ProductionReadiness } from './ProductionReadiness';
import styles from './DwtSettingsPage.module.css';
import shared from '../../dwt/dwt.module.css';

const TRANSPORT = ['Road', 'Rail', 'Air', 'Sea', 'Inland Waterway', 'Piped', 'Other'];
const REASONS: { value: DwtReason; label: string }[] = [
  { value: 'ON_SITE', label: 'Movement within the same premises' },
  { value: 'HOUSEHOLD', label: 'Householder transporting own waste' },
  { value: 'ONE_OFF', label: 'One-off movement' },
  { value: 'MARINE', label: 'Marine transport' },
];

export const MODE_OPTIONS: Array<{ value: DwtModeValue; label: string; help: string }> = [
  {
    value: 'off',
    label: 'Off',
    help: 'Nothing is reported to Defra. Choose this while setting up, or to stop reporting.',
  },
  {
    value: 'manual',
    label: 'Manual',
    help: 'Nothing is sent by itself. An admin selects receipts and queues them, or submits one at a time.',
  },
  {
    value: 'auto_on_approval',
    label: 'Automatic on approval',
    help: 'Each receipt is reported to Defra shortly after it is approved, and corrections are sent automatically.',
  },
];

export const RECEIVED_AT_OPTIONS: Array<{ value: DwtReceivedAtSource; label: string }> = [
  { value: 'receipt_submitted_at', label: 'When the receipt was submitted' },
  { value: 'receipt_created_at', label: 'When the receipt was created' },
  { value: 'approved_at', label: 'When the receipt was approved' },
];

interface FormState {
  enabled: boolean;
  environment: 'test' | 'production';
  apiCode: string;
  mode: DwtModeValue;
  startDate: string;
  receivedAt: DwtReceivedAtSource;
  allowImported: boolean;
  maxWeight: string;
  receiverSiteName: string;
  receiverAuth: string;
  receiverAddress: string;
  receiverPostcode: string;
  carrierName: string;
  carrierReg: string;
  carrierReason: DwtReason | '';
  transport: string;
  carrierVehicle: string;
}

function toForm(d: DwtSettingsDto): FormState {
  return {
    enabled: d.enabled,
    environment: d.environment,
    apiCode: '',
    mode: d.dwt_mode ?? 'off',
    startDate: d.dwt_start_date ?? '',
    receivedAt: d.received_at_source ?? 'receipt_submitted_at',
    allowImported: d.allow_imported_receipts ?? false,
    maxWeight: String(d.max_weight_kg ?? 100000),
    receiverSiteName: d.receiver_site_name ?? '',
    receiverAuth: d.receiver_authorisation_number ?? '',
    receiverAddress: d.receiver_address_full ?? '',
    receiverPostcode: d.receiver_postcode ?? '',
    carrierName: d.carrier_organisation_name ?? '',
    carrierReg: d.carrier_registration_number ?? '',
    carrierReason: d.carrier_reason_no_registration ?? '',
    transport: d.carrier_means_of_transport,
    carrierVehicle: d.carrier_default_vehicle_registration ?? '',
  };
}

const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** What blocks saving, in plain words. */
export function settingsProblems(f: FormState): string[] {
  const out: string[] = [];
  if (f.carrierReg.trim() !== '' && f.carrierReason !== '') {
    out.push('Provide either a carrier registration number or a reason, not both.');
  }
  if (f.mode !== 'off' && f.startDate.trim() === '') {
    out.push('Set a start date before turning reporting on. Receipts dated before it are never reported.');
  }
  const reg = f.carrierVehicle.replace(/\s+/g, '');
  if (reg.length > 10 || (reg !== '' && !/^[A-Za-z0-9]+$/.test(reg))) {
    out.push('The default vehicle registration can only contain letters and numbers (at most 10).');
  }
  const w = Number(f.maxWeight);
  if (!Number.isFinite(w) || w <= 0) out.push('The weight limit must be greater than 0.');
  return out;
}

export interface ChecklistItem {
  key: string;
  label: string;
  met: boolean;
  detail: string;
}

/** The go-live prerequisites. Booleans and counts only: no secrets. */
export function buildChecklist(
  dto: DwtSettingsDto,
  profiles: { confirmed: number; unconfirmed: number; total: number } | null,
): ChecklistItem[] {
  const site = [dto.receiver_site_name, dto.receiver_authorisation_number, dto.receiver_address_full, dto.receiver_postcode].every(
    (v) => v !== null && v.trim() !== '',
  );
  const carrier =
    dto.carrier_organisation_name !== null &&
    dto.carrier_organisation_name.trim() !== '' &&
    (dto.carrier_registration_number !== null || dto.carrier_reason_no_registration !== null);
  const prod = dto.environment === 'production';
  const creds = dto.vendor_credentials[dto.environment];
  return [
    {
      key: 'api_code',
      label: 'Defra API code entered',
      met: dto.api_code_set,
      detail: dto.api_code_set ? 'An API code is saved.' : 'Enter the API code the site owner got when registering with Defra.',
    },
    {
      key: 'site',
      label: 'Receiver site details complete',
      met: site,
      detail: site ? 'Site name, permit number, address and postcode are set.' : 'Fill in the site name, permit number, address and postcode.',
    },
    {
      key: 'carrier',
      label: 'Carrier details complete',
      met: carrier,
      detail: carrier ? 'Carrier name and registration (or reason) are set.' : 'Enter the carrier name and a registration number or a reason for none.',
    },
    {
      key: 'profiles',
      label: 'Product profiles confirmed',
      met: profiles !== null && profiles.confirmed > 0 && profiles.unconfirmed === 0,
      detail:
        profiles === null
          ? 'Could not check the product profiles.'
          : profiles.confirmed === 0
            ? 'No product profile has been confirmed yet.'
            : profiles.unconfirmed > 0
              ? `${profiles.confirmed} confirmed, ${profiles.unconfirmed} still to confirm.`
              : `${profiles.confirmed} confirmed.`,
    },
    {
      key: 'start',
      label: 'Start date set',
      met: dto.dwt_start_date !== null,
      detail: dto.dwt_start_date !== null ? `Receipts dated from ${dto.dwt_start_date} are reported.` : 'Choose the first date to report from.',
    },
    {
      key: 'mode',
      label: 'Reporting mode chosen',
      met: dto.dwt_mode !== 'off',
      detail: dto.dwt_mode === 'off' ? 'Reporting is off.' : dto.dwt_mode === 'manual' ? 'Manual.' : 'Automatic on approval.',
    },
    {
      key: 'environment',
      label: 'Environment is Production',
      met: prod,
      detail: prod ? 'Receipts are reported to Defra’s live service.' : 'Test: nothing real is reported.',
    },
    {
      key: 'credentials',
      label: `Server credentials present (${dto.environment})`,
      met: creds,
      detail: creds ? 'Yes.' : 'No. Ask support to add the Defra credentials to the server.',
    },
    {
      key: 'live',
      label: 'Live reporting allowed on this server',
      met: !prod || dto.production_calls_allowed,
      detail: !prod ? 'Not needed for the test environment.' : dto.production_calls_allowed ? 'Yes.' : 'No. Support must switch it on at go-live.',
    },
  ];
}

export function DwtSettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dto, setDto] = useState<DwtSettingsDto | null>(null);
  const [form, setForm] = useState<FormState | null>(null);

  const products = useQuery({ queryKey: ['dwt', 'profile-products'], queryFn: fetchProfileProducts, retry: false, staleTime: 30_000 });
  const profileCounts = useMemo(() => {
    if (!products.data) return null;
    const confirmed = products.data.filter((p) => p.status === 'confirmed').length;
    const unconfirmed = products.data.filter((p) => p.status === 'unconfirmed').length;
    return { confirmed, unconfirmed, total: products.data.length };
  }, [products.data]);

  useEffect(() => {
    let cancelled = false;
    fetchDwtSettings()
      .then((d) => {
        if (cancelled) return;
        setDto(d);
        setForm(toForm(d));
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load Digital Waste Tracking settings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  async function handleSave() {
    if (!form) return;
    const problems = settingsProblems(form);
    if (problems.length > 0) {
      toast.error(problems[0]!);
      return;
    }
    setSaving(true);
    try {
      const saved = await saveDwtSettings({
        enabled: form.enabled,
        environment: form.environment,
        ...(form.apiCode.trim() !== '' ? { api_code: form.apiCode.trim() } : {}),
        receiver_site_name: orNull(form.receiverSiteName),
        receiver_authorisation_number: orNull(form.receiverAuth),
        receiver_address_full: orNull(form.receiverAddress),
        receiver_postcode: orNull(form.receiverPostcode),
        carrier_organisation_name: orNull(form.carrierName),
        carrier_registration_number: orNull(form.carrierReg),
        carrier_reason_no_registration: form.carrierReason === '' ? null : form.carrierReason,
        carrier_means_of_transport: form.transport,
        dwt_start_date: orNull(form.startDate),
        dwt_mode: form.mode,
        received_at_source: form.receivedAt,
        allow_imported_receipts: form.allowImported,
        max_weight_kg: Number(form.maxWeight),
        carrier_default_vehicle_registration: orNull(form.carrierVehicle.replace(/\s+/g, '').toUpperCase()),
      });
      setDto(saved);
      setForm(toForm(saved));
      toast.success('Digital Waste Tracking settings saved');
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } } | null)?.response?.data?.message;
      toast.error(msg ?? 'Failed to save settings. Check the API code is a valid UUID.');
    } finally {
      setSaving(false);
    }
  }

  if (loading || !form || !dto) {
    return (
      <div className={styles.page}>
        <PageHeader title="Digital Waste Tracking" />
        <Card>
          <div className={styles.spinnerWrap}>
            <Spinner />
          </div>
        </Card>
      </div>
    );
  }

  const checklist = buildChecklist(dto, profileCounts);
  const unmet = checklist.filter((c) => !c.met).length;
  const problems = settingsProblems(form);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Digital Waste Tracking"
        subtitle="Receiver and carrier details, and how receipts are reported to Defra."
        actions={
          <div className={shared.row}>
            <Button variant="secondary" onClick={() => navigate('/settings/dwt/profiles')}>
              Product profiles
            </Button>
            <Button variant="secondary" onClick={() => navigate('/collections/dwt')}>
              Dashboard
            </Button>
            <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/settings')}>
              Back
            </Button>
          </div>
        }
      />

      <Card title="Go-live checklist">
        <div className={shared.stack}>
          <p className={shared.muted} style={{ margin: 0 }} data-testid="checklist-summary">
            {unmet === 0 ? 'Everything needed to go live is in place.' : `${unmet} of ${checklist.length} still to do.`}
          </p>
          <ul className={shared.list} style={{ listStyle: 'none', paddingLeft: 0 }} aria-label="Go-live checklist">
            {checklist.map((c) => (
              <li key={c.key} data-met={c.met ? 'true' : 'false'} className={shared.row}>
                {c.met ? <Check size={16} aria-label="Done" /> : <CircleDashed size={16} aria-label="To do" />}
                <span>
                  <strong>{c.label}.</strong> <span className={shared.muted}>{c.detail}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className={shared.muted} style={{ margin: 0 }}>
            <ListChecks size={14} aria-hidden /> Product profiles are managed under{' '}
            <Link to="/settings/dwt/profiles">Product profiles</Link>.
          </p>
        </div>
      </Card>

      <ProductionReadiness />

      <Card title="Reporting">
        <div className={styles.form}>
          <label className={styles.check}>
            <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            Enable Digital Waste Tracking for this company
          </label>

          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend className={shared.muted}>Mode</legend>
            {MODE_OPTIONS.map((m) => (
              <div key={m.value} className={styles.field} style={{ marginBottom: '0.5rem' }}>
                <label className={styles.check}>
                  <input
                    type="radio"
                    name="dwt-mode"
                    value={m.value}
                    checked={form.mode === m.value}
                    onChange={() => set('mode', m.value)}
                    aria-describedby={`dwt-mode-help-${m.value}`}
                  />
                  <strong>{m.label}</strong>
                </label>
                <small id={`dwt-mode-help-${m.value}`}>{m.help}</small>
              </div>
            ))}
          </fieldset>

          <div className={styles.field}>
            <label htmlFor="dwt-start">Start date</label>
            <input
              id="dwt-start"
              type="date"
              value={form.startDate}
              onChange={(e) => set('startDate', e.target.value)}
              aria-invalid={form.mode !== 'off' && form.startDate === '' ? true : undefined}
            />
            <small>
              Required before reporting can be turned on. Receipts dated before this day are never reported, even if you
              queue them yourself.
            </small>
          </div>

          <div className={styles.field}>
            <label htmlFor="dwt-received-at">Time received, sent to Defra, comes from</label>
            <select
              id="dwt-received-at"
              value={form.receivedAt}
              onChange={(e) => set('receivedAt', e.target.value as DwtReceivedAtSource)}
            >
              {RECEIVED_AT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <small>This time also starts the 2 working day deadline for reporting the receipt.</small>
          </div>

          <label className={styles.check}>
            <input type="checkbox" checked={form.allowImported} onChange={(e) => set('allowImported', e.target.checked)} />
            Also report receipts imported from the old system
          </label>

          <div className={styles.field}>
            <label htmlFor="dwt-max-weight">Largest weight accepted (kg)</label>
            <input id="dwt-max-weight" inputMode="decimal" value={form.maxWeight} onChange={(e) => set('maxWeight', e.target.value)} />
            <small>A weight above this is refused when entered, to catch typing mistakes.</small>
          </div>
        </div>
      </Card>

      <Card title="Defra connection">
        <div className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="dwt-env">Environment</label>
            <select
              id="dwt-env"
              value={form.environment}
              onChange={(e) => set('environment', e.target.value as 'test' | 'production')}
            >
              <option value="test">Test</option>
              <option value="production">Production</option>
            </select>
            <small>
              Server credentials: test {dto.vendor_credentials.test ? 'configured' : 'missing'}, production{' '}
              {dto.vendor_credentials.production ? 'configured' : 'missing'}. Production calls are{' '}
              {dto.production_calls_allowed ? 'allowed' : 'blocked'} on this server.
            </small>
          </div>

          <div className={styles.field}>
            <label htmlFor="dwt-apicode">API code</label>
            <input
              id="dwt-apicode"
              type="password"
              autoComplete="off"
              placeholder={dto.api_code_set ? `Stored (${dto.api_code_masked ?? ''}). Leave blank to keep.` : 'UUID'}
              value={form.apiCode}
              onChange={(e) => set('apiCode', e.target.value)}
            />
            <small>
              The code the site owner receives when they register with Defra. It is saved encrypted and cannot be read back.
            </small>
          </div>
        </div>
      </Card>

      <Card title="Receiver site">
        <div className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="dwt-site">Site name</label>
            <input id="dwt-site" value={form.receiverSiteName} maxLength={255} onChange={(e) => set('receiverSiteName', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label htmlFor="dwt-auth">Permit / authorisation number</label>
            <input id="dwt-auth" value={form.receiverAuth} maxLength={100} onChange={(e) => set('receiverAuth', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label htmlFor="dwt-addr">Site address</label>
            <input id="dwt-addr" value={form.receiverAddress} maxLength={500} onChange={(e) => set('receiverAddress', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label htmlFor="dwt-pc">Postcode</label>
            <input id="dwt-pc" value={form.receiverPostcode} maxLength={12} onChange={(e) => set('receiverPostcode', e.target.value)} />
          </div>
        </div>
      </Card>

      <Card title="Carrier defaults">
        <div className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="dwt-cname">Organisation name</label>
            <input id="dwt-cname" value={form.carrierName} maxLength={255} onChange={(e) => set('carrierName', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label htmlFor="dwt-creg">Registration number</label>
            <input id="dwt-creg" value={form.carrierReg} maxLength={100} onChange={(e) => set('carrierReg', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label htmlFor="dwt-creason">Reason for no registration</label>
            <select id="dwt-creason" value={form.carrierReason} onChange={(e) => set('carrierReason', e.target.value as DwtReason | '')}>
              <option value="">None</option>
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label htmlFor="dwt-transport">Default means of transport</label>
            <select id="dwt-transport" value={form.transport} onChange={(e) => set('transport', e.target.value)}>
              {TRANSPORT.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label htmlFor="dwt-cvehicle">Default carrier vehicle registration</label>
            <input
              id="dwt-cvehicle"
              value={form.carrierVehicle}
              maxLength={12}
              onChange={(e) => set('carrierVehicle', e.target.value)}
            />
            <small>
              Used when a receipt has no vehicle of its own and the driver has none. Leave empty to require a vehicle on every
              receipt.
            </small>
          </div>
        </div>
      </Card>

      <div className={styles.actions} style={{ gap: '1rem', alignItems: 'center' }}>
        {problems.length > 0 ? (
          <span role="alert" className={shared.fieldError}>
            {problems[0]}
          </span>
        ) : null}
        <Button icon={Save} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

export default DwtSettingsPage;
